import { useCallback, useEffect, useRef, useState } from 'react';

type UseSystemAudioPreviewResult = {
	stream: MediaStream | null;
	error: string | null;
	isActivating: boolean;
	activate: () => Promise<void>;
	replace: () => Promise<void>;
	deactivate: () => void;
	release: () => void;
};

const resolveDisplayCaptureError = (error: unknown): string => {
	if (error instanceof DOMException) {
		if (error.name === 'NotAllowedError') {
			return 'Permita compartilhar a tela ou aba com áudio do sistema habilitado.';
		}
	}
	if (error instanceof Error && error.message.trim()) {
		return error.message;
	}
	return 'Não foi possível compartilhar a tela com áudio do sistema.';
};

const requestSystemAudioStream = async (): Promise<MediaStream> => {
	if (!navigator.mediaDevices?.getDisplayMedia) {
		throw new Error(
			'O compartilhamento de áudio do sistema não é suportado neste navegador ou ambiente.',
		);
	}

	const displayStream = await navigator.mediaDevices.getDisplayMedia({
		video: true,
		audio: true,
	});

	displayStream.getVideoTracks().forEach((track) => {
		track.stop();
		displayStream.removeTrack(track);
	});

	if (displayStream.getAudioTracks().length === 0) {
		displayStream.getTracks().forEach((track) => track.stop());
		throw new Error(
			'Compartilhe a tela ou aba com áudio do sistema habilitado.',
		);
	}

	return displayStream;
};

export const useSystemAudioPreview = (): UseSystemAudioPreviewResult => {
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isActivating, setIsActivating] = useState(false);
	const streamRef = useRef<MediaStream | null>(null);

	const stopStream = useCallback(() => {
		streamRef.current?.getTracks().forEach((track) => track.stop());
		streamRef.current = null;
		setStream(null);
	}, []);

	const activateInternal = useCallback(async () => {
		setIsActivating(true);
		try {
			const nextStream = await requestSystemAudioStream();
			stopStream();
			streamRef.current = nextStream;
			setStream(nextStream);
			setError(null);
		} catch (unknownError) {
			setError(resolveDisplayCaptureError(unknownError));
		} finally {
			setIsActivating(false);
		}
	}, [stopStream]);

	const activate = useCallback(async () => {
		await activateInternal();
	}, [activateInternal]);

	const replace = useCallback(async () => {
		await activateInternal();
	}, [activateInternal]);

	const deactivate = useCallback(() => {
		stopStream();
		setError(null);
	}, [stopStream]);

	const release = useCallback(() => {
		streamRef.current = null;
		setStream(null);
		setError(null);
	}, []);

	useEffect(() => {
		return () => {
			streamRef.current?.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		};
	}, []);

	return {
		stream,
		error,
		isActivating,
		activate,
		replace,
		deactivate,
		release,
	};
};
